import React, { useEffect, useMemo, useState } from "react";
import { Newspaper, RefreshCw, Loader2, Clock, Save, Check, ArrowDownWideNarrow } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const CAT_COLORS = {
  "Marché": "bg-brand-lime/30 text-brand-greenDeep",
  "Crédit": "bg-amber-100 text-amber-700",
  "Réglementation": "bg-blue-100 text-blue-700",
  "Location": "bg-emerald-100 text-emerald-700",
  "Investissement": "bg-purple-100 text-purple-700",
};
const DEFAULT_CATS = ["Marché", "Crédit", "Réglementation", "Location", "Investissement"];

const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return s; }
};

export default function Actualites() {
  const [items, setItems] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [available, setAvailable] = useState(DEFAULT_CATS);
  const [activeCats, setActiveCats] = useState([]); // [] = toutes
  const [savedCats, setSavedCats] = useState([]);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [sort, setSort] = useState("recent");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/news");
      setItems(r.data.items || []);
      setGeneratedAt(r.data.generated_at);
    } catch (e) { toast.error("Impossible de charger les actualités"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/news/preferences").then((r) => {
      const av = r.data?.available?.length ? r.data.available : DEFAULT_CATS;
      setAvailable(av);
      const saved = r.data?.categories || [];
      setSavedCats(saved);
      setActiveCats(saved);
    }).catch(() => {});
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/news/refresh");
      setItems(r.data.items || []);
      setGeneratedAt(r.data.generated_at);
      toast.success("Actualités mises à jour");
    } catch (e) { toast.error("Échec de la mise à jour"); }
    finally { setRefreshing(false); }
  };

  const toggleCat = (c) =>
    setActiveCats((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      const r = await api.put("/news/preferences", { categories: activeCats });
      setSavedCats(r.data?.categories || []);
      toast.success(activeCats.length ? "Préférences enregistrées" : "Toutes les catégories affichées");
    } catch (e) { toast.error("Échec de l'enregistrement"); }
    finally { setSavingPrefs(false); }
  };

  const prefsDirty = useMemo(() => {
    const a = [...activeCats].sort().join("|");
    const b = [...savedCats].sort().join("|");
    return a !== b;
  }, [activeCats, savedCats]);

  const visible = useMemo(() => {
    let list = activeCats.length ? items.filter((it) => activeCats.includes(it.category)) : [...items];
    const byDate = (a, b) => new Date(a.date || 0) - new Date(b.date || 0);
    if (sort === "recent") list.sort((a, b) => byDate(b, a));
    else if (sort === "old") list.sort(byDate);
    else if (sort === "relevance") list.sort((a, b) => (b.relevance || 0) - (a.relevance || 0));
    return list;
  }, [items, activeCats, sort]);

  return (
    <div className="space-y-6" data-testid="actualites-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><Newspaper className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-2xl font-semibold text-brand-greenDeep">Actualités immobilières</h2>
            <p className="text-sm text-brand-green/55 flex items-center gap-1.5">
              {generatedAt ? <><Clock className="w-3.5 h-3.5" /> Actualisé le {fmtDate(generatedAt)}</> : "Le marché immobilier français en temps réel"}
            </p>
          </div>
        </div>
        <button onClick={refresh} disabled={refreshing} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold transition-colors disabled:opacity-60" data-testid="news-refresh-btn">
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Actualiser
        </button>
      </div>

      {/* Filtres & tri */}
      <div className="bg-white rounded-2xl border border-brand-soft p-4" data-testid="news-filters">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex flex-wrap items-center gap-2" data-testid="news-cat-filters">
            <button
              onClick={() => setActiveCats([])}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${activeCats.length === 0 ? "bg-brand-greenDeep text-white" : "bg-brand-softer text-brand-green/70 hover:bg-brand-soft"}`}
              data-testid="news-cat-all"
            >Toutes</button>
            {available.map((c) => {
              const on = activeCats.includes(c);
              return (
                <button
                  key={c}
                  onClick={() => toggleCat(c)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors inline-flex items-center gap-1 ${on ? (CAT_COLORS[c] || "bg-brand-lime/30 text-brand-greenDeep") + " ring-1 ring-brand-limeDark" : "bg-brand-softer text-brand-green/60 hover:bg-brand-soft"}`}
                  data-testid={`news-cat-${c}`}
                >
                  {on && <Check className="w-3 h-3" />} {c}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-sm text-brand-green/60">
              <ArrowDownWideNarrow className="w-4 h-4" />
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-brand-softer rounded-lg px-2 py-1.5 text-sm text-brand-greenDeep outline-none border border-brand-soft" data-testid="news-sort-select">
                <option value="recent">Date (plus récente)</option>
                <option value="old">Date (plus ancienne)</option>
                <option value="relevance">Pertinence</option>
              </select>
            </div>
            <button
              onClick={savePrefs}
              disabled={savingPrefs || !prefsDirty}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-sm font-semibold transition-colors disabled:opacity-50"
              data-testid="news-save-prefs-btn"
            >
              {savingPrefs ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer mes préférences
            </button>
          </div>
        </div>
        {savedCats.length > 0 && (
          <p className="text-xs text-brand-green/45 mt-2" data-testid="news-prefs-hint">Vos préférences : {savedCats.join(", ")} — vous ne voyez que ces catégories.</p>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-brand-green/50"><Loader2 className="w-7 h-7 animate-spin" /></div>
      ) : visible.length === 0 ? (
        <div className="text-center py-24 text-brand-green/50" data-testid="news-empty">
          {items.length === 0 ? "Aucune actualité pour le moment. Cliquez sur « Actualiser »." : "Aucune actualité pour les catégories sélectionnées."}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {visible.map((it) => (
            <article key={it.id} className="bg-white rounded-3xl border border-brand-soft p-6 flex flex-col" data-testid="news-card">
              <div className="flex items-center justify-between gap-3">
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${CAT_COLORS[it.category] || "bg-brand-softer text-brand-greenDeep"}`}>{it.category}</span>
                <span className="text-xs text-brand-green/45">{fmtDate(it.date)}</span>
              </div>
              <h3 className="font-display text-lg font-semibold text-brand-greenDeep mt-3 leading-snug">{it.title}</h3>
              <p className="text-sm text-brand-green/70 mt-2 leading-relaxed flex-1">{it.summary}</p>
              {it.url ? (
                <a href={it.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-limeDark hover:text-brand-greenDeep mt-3" data-testid="news-link">
                  Lire l'article →
                </a>
              ) : null}
              <div className="flex items-center justify-between mt-4">
                {it.source ? <p className="text-xs text-brand-green/45">Source : {it.source}</p> : <span />}
                {typeof it.relevance === "number" && sort === "relevance" && (
                  <span className="text-[11px] font-semibold text-brand-limeDark bg-brand-lime/20 px-2 py-0.5 rounded-full">Pertinence {it.relevance}</span>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
      <p className="text-xs text-brand-green/40 text-center">Actualités générées par IA à titre informatif — vérifiez les sources officielles avant toute décision.</p>
    </div>
  );
}
